import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowTopRightOnSquareIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

import { sitePath } from "../../../utils/urls";
import { STATUS_META, STATUS_ORDER } from "./constants";
import {
  Drawer,
  FilterChip,
  FixCheckboxRow,
  FixFooter,
  GradeDonut,
  ReportTypeIcon,
  StatusCounts,
  StatusPill,
  useFixSelection,
  VitalsGauges,
} from "./shared";
import {
  formatTemplatePath,
  formatVital,
  tallyChecks,
  vitalsFromChecks,
  VITAL_CODES,
  VITAL_SHORT,
} from "./utils";

export const IssueDrawer = ({ issue, project, actionable, onFix, onClose }) => {
  const [pageFilter, setPageFilter] = useState("issues");

  useEffect(() => {
    const hasIssues = (issue?.fail_count || 0) > 0 || (issue?.warn_count || 0) > 0;
    setPageFilter(hasIssues ? "issues" : "all");
  }, [issue?.key, issue?.fail_count, issue?.warn_count]);

  const items = useMemo(() => {
    const rows = (issue?.pages || []).map((page) => ({
      hash: page.suggestion,
      severity: page.status,
      status: page.suggestion_status || (page.suggestion ? "open" : ""),
      agent_type: page.agent_type || (page.suggestion ? "coding" : ""),
      path: page.site_page_path,
      preview_path: page.preview_path || "",
      detail: page.detail,
      evidence_value: page.evidence_value,
      probability_pct: page.probability_pct,
    }));
    return rows.slice().sort(
      (a, b) => (STATUS_ORDER[a.severity] ?? 9) - (STATUS_ORDER[b.severity] ?? 9),
    );
  }, [issue]);

  const visible = useMemo(() => {
    if (pageFilter === "all") return items;
    return items.filter((item) => item.severity === "fail" || item.severity === "warn");
  }, [items, pageFilter]);

  const { selected, toggle, canFix } = useFixSelection(items, actionable);
  const checkCode = issue?.check_code || "";

  return (
    <Drawer
      title={issue.label}
      subtitle={`${issue.fail_count} fail · ${issue.warn_count} warn`}
      onClose={onClose}
      footer={(
        <FixFooter
          actionable={actionable}
          canFix={canFix}
          count={selected.length}
          onFix={() => onFix(selected)}
        />
      )}
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <ReportTypeIcon type={issue.report_type} />
        {items.length > 0 && (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <FilterChip
              active={pageFilter === "issues"}
              onClick={() => setPageFilter("issues")}
            >
              Fail & warn
            </FilterChip>
            <FilterChip
              active={pageFilter === "all"}
              onClick={() => setPageFilter("all")}
            >
              All checked
            </FilterChip>
          </div>
        )}
      </div>
      {issue.help && (
        <p className="mb-4 text-sm text-theme-secondary-text">{issue.help}</p>
      )}
      {visible.length > 0 ? (
        <div className="space-y-2">
          {visible.map((item) => {
            const metric = VITAL_CODES.includes(checkCode) && item.evidence_value != null
              ? formatVital(checkCode, item.evidence_value)
              : null;
            const pathLabel = formatTemplatePath(item.path) || item.path || "Site-level";
            const previewTo = item.preview_path && project
              ? `${sitePath(project, "preview/")}?path=${encodeURIComponent(item.preview_path)}`
              : null;
            return (
              <FixCheckboxRow
                key={`${item.hash || "none"}-${item.path}-${item.severity}`}
                item={item}
                checked={selected.includes(item.hash)}
                actionable={actionable}
                onToggle={toggle}
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    {previewTo ? (
                      <Link
                        to={previewTo}
                        title="See page"
                        className="inline-flex max-w-full items-center gap-1.5 text-sm font-medium text-theme-primary hover:underline"
                      >
                        <span className="truncate">{pathLabel}</span>
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0" />
                      </Link>
                    ) : (
                      <p className="truncate text-sm font-medium">{pathLabel}</p>
                    )}
                    {item.agent_type === "data" && (
                      <p className="mt-1 text-xs text-theme-secondary-text">
                        Data agent is not wired yet.
                      </p>
                    )}
                    {!metric && (item.detail || item.probability_pct != null) && (
                      <p className="mt-0.5 text-xs text-theme-secondary-text">
                        {item.detail}
                        {item.probability_pct != null && (
                          <span className="tabular-nums">
                            {item.detail ? " · " : ""}
                            {item.probability_pct}% {item.severity}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                  <span className="flex shrink-0 items-center gap-1.5 pt-0.5">
                    {metric && (
                      <span className={`text-xs font-medium tabular-nums ${
                        STATUS_META[item.severity]?.text || "text-theme-secondary-text"
                      }`}
                      >
                        {metric}
                      </span>
                    )}
                    <StatusPill status={item.severity} />
                  </span>
                </div>
              </FixCheckboxRow>
            );
          })}
        </div>
      ) : (
        <p className="rounded-lg border border-theme-secondary px-3 py-6 text-center text-sm text-theme-secondary-text">
          {pageFilter === "all"
            ? "No pages checked for this."
            : "No failing pages for this check."}
        </p>
      )}
    </Drawer>
  );
};

export const PageDrawer = ({
  title,
  previewPath,
  project,
  checks = [],
  samples = [],
  focusCheck,
  actionable,
  suggestionsByCheck = {},
  onFix,
  onClose,
}) => {
  const fixItems = checks
    .filter((c) => c.status === "fail" || c.status === "warn")
    .map((check) => {
      const suggestion = suggestionsByCheck[check.code];
      return {
        hash: suggestion?.hash,
        severity: check.status,
        status: suggestion?.status || "open",
        agent_type: suggestion?.agent_type || "coding",
        code: check.code,
        label: check.label,
      };
    })
    .filter((item) => item.hash);

  const { selected, toggle, canFix } = useFixSelection(fixItems, actionable);
  const issues = checks
    .filter((c) => c.status === "fail" || c.status === "warn")
    .sort((a, b) => (a.status === "fail" ? 0 : 1) - (b.status === "fail" ? 0 : 1));
  const counts = tallyChecks(checks);
  const pageVitals = vitalsFromChecks(checks);
  // Detail templates: prefer a built sample URL over the collection list route.
  const seePath = samples[0]?.path || previewPath;
  const previewTo = seePath && project
    ? `${sitePath(project, "preview/")}?path=${encodeURIComponent(seePath)}`
    : null;
  const seePage = previewTo ? (
    <Link
      to={previewTo}
      className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-theme-secondary px-3 py-2 text-sm font-medium text-theme-text transition hover:bg-theme-secondary"
    >
      See page
      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
    </Link>
  ) : null;

  return (
    <Drawer
      title={title}
      onClose={onClose}
      footer={(
        <FixFooter
          actionable={actionable}
          canFix={canFix}
          count={selected.length}
          onFix={() => onFix(selected)}
          leading={seePage}
        />
      )}
    >
      {(counts.pass + counts.warn + counts.fail + counts.skipped) > 0 && (
        <div className="mb-4 flex items-center justify-between gap-3">
          <GradeDonut
            counts={counts}
            center={counts.fail + counts.warn}
            caption="issues"
            compact
          />
          <StatusCounts counts={counts} labeled />
        </div>
      )}

      {pageVitals.length > 0 && (
        <div className="mb-4">
          <VitalsGauges
            vitals={Object.fromEntries(pageVitals.map((v) => [v.code, v.value]))}
          />
        </div>
      )}

      {issues.length > 0 ? (
        <>
          <h3 className="mb-2 text-xs font-medium uppercase text-theme-secondary-text">Page issues</h3>
          <ul className="rounded-xl border border-theme-secondary">
          {issues.map((check, index) => {
            const fixItem = fixItems.find((item) => item.code === check.code);
            const selectable = !!fixItem;
            return (
              <li
                key={check.code}
                className={index > 0 ? "border-t border-theme-secondary" : ""}
              >
                <CheckRow
                  check={check}
                  defaultOpen={focusCheck === check.code || check.status === "fail"}
                  checkbox={selectable ? (
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={selected.includes(fixItem.hash)}
                      disabled={!actionable || fixItem.status !== "open"}
                      onChange={() => toggle(fixItem.hash, check.status)}
                    />
                  ) : null}
                />
              </li>
            );
          })}
          </ul>
        </>
      ) : (
        <div className="flex flex-col items-center rounded-xl border border-theme-secondary px-4 py-10 text-center">
          <STATUS_META.pass.Icon className={`mb-2 h-8 w-8 ${STATUS_META.pass.text}`} />
          <p className="text-sm font-medium text-theme-text">No issues. Congrats!</p>
        </div>
      )}

      {samples.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-xs font-medium uppercase text-theme-secondary-text">Samples</h3>
          <ul className="space-y-1">
            {samples.map((sample) => {
              const to = sample.path && project
                ? `${sitePath(project, "preview/")}?path=${encodeURIComponent(sample.path)}`
                : null;
              const label = sample.label || sample.path;
              if (!to) {
                return (
                  <li key={sample.hash || sample.path}>
                    <span className="block truncate rounded-lg border border-theme-secondary px-2.5 py-1.5 text-xs text-theme-secondary-text">
                      {label}
                    </span>
                  </li>
                );
              }
              return (
                <li key={sample.hash || sample.path}>
                  <Link
                    to={to}
                    className="flex items-center gap-2 rounded-lg border border-theme-secondary px-2.5 py-1.5 text-xs text-theme-text transition hover:bg-theme-secondary"
                  >
                    <span className="min-w-0 flex-1 truncate">{label}</span>
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0 text-theme-secondary-text" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Drawer>
  );
};

export const SuggestionDrawer = ({ group, project, actionable, onFix, onClose }) => {
  const items = (group?.items || []).map((item) => ({
    hash: item.hash,
    severity: item.severity || "warn",
    status: item.status,
    agent_type: item.agent_type,
    path: item.site_page_path,
    route: item.site_page_route,
    title: item.title,
    prompt: item.prompt,
  }));
  const { selected, toggle, canFix } = useFixSelection(items, actionable);
  const [expanded, setExpanded] = useState(null);

  return (
    <Drawer
      title={group.title}
      subtitle={`${group.items.length} suggestions`}
      onClose={onClose}
      footer={(
        <FixFooter
          actionable={actionable}
          canFix={canFix}
          count={selected.length}
          onFix={() => onFix(selected)}
        />
      )}
    >
      {group.help && (
        <p className="mb-4 text-sm text-theme-secondary-text">{group.help}</p>
      )}
      <div className="space-y-2">
        {items.map((item) => {
          const previewTo = item.route && project
            ? `${sitePath(project, "preview/")}?path=${encodeURIComponent(item.route)}`
            : null;
          return (
            <div key={item.hash} className="rounded-lg border border-theme-secondary">
              <FixCheckboxRow
                item={item}
                checked={selected.includes(item.hash)}
                actionable={actionable}
                onToggle={toggle}
              >
                <button
                  type="button"
                  className="flex w-full items-start justify-between gap-2 text-left"
                  onClick={() => setExpanded(expanded === item.hash ? null : item.hash)}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <StatusPill status={item.severity} />
                      <span className="truncate text-sm font-medium">
                        {item.path || item.title}
                      </span>
                    </div>
                    {item.agent_type === "data" && (
                      <p className="mt-1 text-xs text-theme-secondary-text">
                        Data agent is not wired yet.
                      </p>
                    )}
                  </div>
                  <ChevronDownIcon
                    className={`h-4 w-4 shrink-0 text-theme-secondary-text transition ${
                      expanded === item.hash ? "" : "-rotate-90"
                    }`}
                  />
                </button>
              </FixCheckboxRow>
              {expanded === item.hash && (
                <div className="space-y-2 border-t border-theme-secondary px-3 py-2">
                  {previewTo && (
                    <Link
                      to={previewTo}
                      className="inline-flex items-center gap-1 rounded-lg border border-theme-secondary px-2.5 py-1 text-xs font-medium text-theme-text transition hover:bg-theme-secondary"
                    >
                      See page
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    </Link>
                  )}
                  {item.prompt && (
                    <p className="text-xs text-theme-secondary-text whitespace-pre-wrap">
                      {item.prompt}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Drawer>
  );
};

const CheckRow = ({ check, defaultOpen, checkbox }) => {
  const [open, setOpen] = useState(defaultOpen);
  const meta = STATUS_META[check.status] || STATUS_META.skipped;
  const vitalValue = check.evidence?.value != null && VITAL_SHORT[check.code]
    ? formatVital(check.code, check.evidence.value)
    : null;
  const probs = check.evidence?.probabilities || {};
  const statusProb = probs[check.status];
  const probabilityPct = statusProb != null
    ? Math.round(Number(statusProb) * 100)
    : null;

  return (
    <div>
      <div className="flex items-start gap-2 px-3 py-2.5">
        {checkbox}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-start justify-between gap-2 text-left"
        >
          <div className="min-w-0">
            <span className="text-sm font-medium">{check.label || check.code}</span>
            {!open && !vitalValue && (check.detail || check.help) && (
              <p className="mt-0.5 truncate text-xs text-theme-secondary-text">
                {check.detail || check.help}
              </p>
            )}
          </div>
          <span className="flex shrink-0 items-center gap-1.5">
            {vitalValue && (
              <span className={`text-xs font-medium tabular-nums ${meta.text}`}>
                {vitalValue}
              </span>
            )}
            {probabilityPct != null && (
              <span className={`text-xs font-medium tabular-nums ${meta.text}`}>
                {probabilityPct}%
              </span>
            )}
            <meta.Icon className={`h-4 w-4 shrink-0 ${meta.text}`} />
            <span className={`text-xs capitalize ${meta.text}`}>{check.status}</span>
            <ChevronDownIcon
              className={`h-4 w-4 text-theme-secondary-text transition ${open ? "" : "-rotate-90"}`}
            />
          </span>
        </button>
      </div>
      {open && (check.detail || check.help) && (
        <div className="border-t border-theme-secondary px-3 py-2.5 text-xs text-theme-secondary-text">
          {vitalValue && (
            <p className={`mb-1 font-medium tabular-nums ${meta.text}`}>
              p75 {vitalValue}
            </p>
          )}
          {check.detail && (
            <p className={`mb-1 font-medium ${meta.text}`}>{check.detail}</p>
          )}
          {check.help && <p>{check.help}</p>}
        </div>
      )}
    </div>
  );
};
